import api, {
  PaginateRequest, PaginateResponse, ResponseCallback
} from '../services/api.service'
import alertService from '../services/alert.service'
import routeUtil from '../utils/route.util'
import { CaseData } from './inventoryCase.api'
import { ConsumedData } from './consumed.api'
import { InventoryData } from './inventory.api'
import { ItemData } from './item.api'
import { MetadataData } from './metadata.api'
import { ShipData } from './ship.api'
import { TransferData } from './transfer.api'
import { LicensePlateNumberData } from './licensePlateNumber.api'
import { LicensePlateApplicableType } from './licensePlate.api'
import { Markup as LabelMarkup } from '../views/LabelBuilder/LabelBuilder.slice'

export interface PalletData {
  id: number
  siteId: number
  itemId: number
  inventoryId?: number
  deletedBy?: number
  number?: number
  deletedAt?: string
  createdAt: string
  updatedAt: string
  code: string
  quantity: number
  isBare: boolean
  isConsumed: boolean
  isDeleted: boolean
  lastInternalTransfer?: TransferData
  canRollback: boolean
  isNewProduction: boolean
  licensePlates: string[]
  unitQuantity?: number
  positions?: number[]
  unusedPositions?: number[]
  item?: ItemData
  attachedLicensePlates?: PalletAttachedLicensePlates
  inventory?: InventoryData
  transfers?: TransferData[]
  ships?: ShipData[]
  consumed?: ConsumedData<PalletData>
  aliveTransfer?: TransferData
  deadTransfer?: TransferData
  licensePlateNumbers?: LicensePlateNumberData[]
}

type PalletPaginateReceivingsResponse = PaginateResponse<PalletData[]>
type PalletPaginateInventoriesResponse = PaginateResponse<PalletData[]>
type PalletAttachedLicensePlates = Record<LicensePlateApplicableType, string[]>

export interface PalletCreateRequest {
  item?: number
}

export interface PalletListByCodesRequest {
  codes?: string[]
}

export interface PalletStatusData {
  name: string
  value: PalletStatus
}

export type PalletStatus = 'inventory' | 'consumed' | 'shipped' | 'bare'

export const statuses: PalletStatusData[] = [
  { name: 'Inventory', value: 'inventory' },
  { name: 'Consumed', value: 'consumed' },
  { name: 'Shipped', value: 'shipped' },
  { name: 'Bare pallets', value: 'bare' }
]

export interface PalletPaginateInventoriesRequest extends PaginateRequest {
  status: PalletStatus
  startAt?: string
  endAt?: string
  item?: string
  workOrder?: string
  code?: string
}

const routes = {
  paginateReceivings: '/pallets/receivings/paginate',
  print: '/pallets/{id}/print',
  rollbackTransfer: '/pallets/{id}/transfers/rollback',
  delete: '/pallets/{id}',
  create: '/pallets',
  readByCode: '/pallets/code/{code}',
  paginateInventories: '/pallets/inventories/paginate',
  read: '/pallets/{id}',
  listByCodes: '/pallets/listByCodes',
  listTransfers: '/pallets/{id}/transfers',
  listCases: '/pallets/{id}/cases',
  listMetadatas: '/pallets/{id}/metadatas',
  readItem: '/pallets/{id}/item'
}

export default {
  paginateReceivings (
    params: PaginateRequest,
    callback: ResponseCallback<PalletPaginateReceivingsResponse>
  ) {
    api.get(routes.paginateReceivings, params, callback)
  },

  print (id: number, callback: ResponseCallback<LabelMarkup[]>) {
    alertService.info('Pallet generating...')
    const url = routeUtil.replaceParams(routes.print, { id })
    api.get(url, {}, callback)
  },

  rollbackTransfer (id: number, callback: ResponseCallback<TransferData>) {
    const url = routeUtil.replaceParams(routes.rollbackTransfer, { id })
    api.post(url, {}, callback)
  },

  delete (id: number, callback: ResponseCallback<PalletData>) {
    const url = routeUtil.replaceParams(routes.delete, { id })
    api.delete(url, {}, callback)
  },

  create (
    payload: PalletCreateRequest,
    callback: ResponseCallback<PalletData>
  ) {
    api.post(routes.create, payload, callback)
  },

  readByCode (code: string, callback: ResponseCallback<PalletData>) {
    const url = routeUtil.replaceParams(routes.readByCode, { code })
    api.get(url, {}, callback)
  },

  paginateInventories (
    params: PalletPaginateInventoriesRequest,
    callback: ResponseCallback<PalletPaginateInventoriesResponse>
  ) {
    api.get(routes.paginateInventories, params, callback)
  },

  read (id: number, callback: ResponseCallback<PalletData>) {
    const url = routeUtil.replaceParams(routes.read, { id })
    api.get(url, {}, callback)
  },

  listTransfers (id: number, callback: ResponseCallback<TransferData[]>) {
    const url = routeUtil.replaceParams(routes.listTransfers, { id })
    api.get(url, {}, callback)
  },

  listByCodes (
    payload: PalletListByCodesRequest,
    callback: ResponseCallback<PalletData[]>) {
    api.post(routes.listByCodes, payload, callback)
  },

  listCases (id: number, callback: ResponseCallback<CaseData[]>) {
    const url = routeUtil.replaceParams(routes.listCases, { id })
    api.get(url, {}, callback)
  },

  listMetadatas (id: number, callback: ResponseCallback<MetadataData[]>) {
    const url = routeUtil.replaceParams(routes.listMetadatas, { id })
    api.get(url, {}, callback)
  },

  readItem (id: number, callback: ResponseCallback<ItemData>) {
    const url = routeUtil.replaceParams(routes.readItem, { id })
    api.get(url, {}, callback)
  }
}
